import {
    Box,
    Chip,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    Paper,
    Typography,
} from '@mui/material';
import MuiModal from '@mui/material/Modal';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { ExportTypes, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import SupportedHotkeys from '../Header/ApplicationQuickMenu/About/SupportedHotkeys';
import WorkspaceIcon from '../Logo/WorkspaceIcon';

export type TabItem = {
    name: JSX.Element | string;
    description: JSX.Element | string;
    content: React.ReactNode;
    type: WorkflowTypes | ExportTypes;
};

type TabModalProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    items: TabItem[];
    selectedPage: WorkflowTypes | ExportTypes | 'QuickHelp' | null;
    setSelectedPage: (
        selectedPage: WorkflowTypes | ExportTypes | 'QuickHelp' | null
    ) => void;
};

export default function AboutTabModal({
    open,
    setOpen,
    items,
    selectedPage,
    setSelectedPage,
}: TabModalProps) {
    // Extract main application (first item) and modules (rest of items)
    const mainApp = items.length > 0 ? items[0] : null;
    const modules = items.length > 1 ? items.slice(1) : [];

    return (
        <MuiModal open={open} onClose={() => setOpen(false)} disableAutoFocus>
            <Box
                sx={(theme) => ({
                    position: 'absolute' as const,
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    ...theme.mixins.frostedSurface,
                    isolation: 'isolate',
                    p: 1,
                })}
            >
                <Box
                    display="grid"
                    gridTemplateColumns="160px 1fr"
                    gap={1}
                    width="60vw"
                    minWidth={800}
                    position="relative"
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-between"
                    >
                        <List sx={{ p: 0, display: 'grid', gap: 1 }}>
                            {/* Main Application Item */}
                            {mainApp && (
                                <>
                                    <ListItem
                                        key={mainApp.type}
                                        sx={{ p: 0 }}
                                        color="primary"
                                    >
                                        <ListItemButton
                                            sx={(theme) => ({
                                                width: '100%',
                                                display: 'grid',
                                                borderRadius: `${theme.shape.borderRadius}px`,
                                                p: 1,
                                                backgroundColor:
                                                    theme.palette.mode ===
                                                    'dark'
                                                        ? 'rgba(0, 180, 229, 0.15)'
                                                        : 'rgba(0, 180, 229, 0.08)',
                                                '&:hover': {
                                                    backgroundColor:
                                                        theme.palette.mode ===
                                                        'dark'
                                                            ? 'rgba(0, 180, 229, 0.25)'
                                                            : 'rgba(0, 180, 229, 0.15)',
                                                },
                                            })}
                                            onClick={() =>
                                                setSelectedPage(mainApp.type)
                                            }
                                            selected={
                                                selectedPage === mainApp.type
                                            }
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <WorkspaceIcon height={12} />
                                                <Typography fontSize={14}>
                                                    {mainApp.name}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                fontSize={11}
                                            >
                                                {mainApp.description}
                                            </Typography>
                                        </ListItemButton>
                                    </ListItem>

                                    {modules.length > 0 && (
                                        <Box mx={1} my={1}>
                                            <Divider>
                                                <Chip
                                                    label="Modules"
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: 11 }}
                                                />
                                            </Divider>
                                        </Box>
                                    )}
                                </>
                            )}

                            {/* Module Items */}
                            {modules.map((item) => (
                                <ListItem
                                    key={item.type}
                                    sx={{ p: 0 }}
                                    color="primary"
                                >
                                    <ListItemButton
                                        sx={(theme) => ({
                                            width: '100%',
                                            display: 'grid',
                                            borderRadius: `${theme.shape.borderRadius}px`,
                                            p: 1,
                                            pl: 2,
                                        })}
                                        color="primary"
                                        onClick={() =>
                                            setSelectedPage(item.type)
                                        }
                                        selected={selectedPage === item.type}
                                    >
                                        <Typography
                                            fontSize={14}
                                            fontWeight={500}
                                        >
                                            {item.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontSize={11}
                                        >
                                            {item.description}
                                        </Typography>
                                    </ListItemButton>
                                </ListItem>
                            ))}

                            <Box mx={1} my={1}>
                                <Divider />
                            </Box>

                            <ListItem sx={{ p: 0 }} color="primary">
                                <ListItemButton
                                    sx={(theme) => ({
                                        width: '100%',
                                        display: 'grid',
                                        borderRadius: `${theme.shape.borderRadius}px`,
                                        p: 1,
                                        pl: 2,
                                    })}
                                    onClick={() => setSelectedPage('QuickHelp')}
                                    color="primary"
                                    selected={selectedPage === 'QuickHelp'}
                                >
                                    <Typography fontSize={14} fontWeight={500}>
                                        <FormattedMessage id="quickHelp" />
                                    </Typography>
                                    <Typography variant="body2" fontSize={11}>
                                        <FormattedMessage id="keyboardShortcuts" />
                                    </Typography>
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Box>

                    <Paper sx={{ p: 3, flex: 1, height: 600 }} elevation={0}>
                        <IconButton
                            onClick={() => setOpen(false)}
                            aria-label="Close"
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                            }}
                        >
                            <X size={21} />
                        </IconButton>

                        {/* Content area with animation */}
                        <AnimatePresence mode="wait">
                            {selectedPage === 'QuickHelp' ? (
                                <motion.div
                                    style={{ height: '100%' }}
                                    key="QuickHelp"
                                    initial={{ opacity: 0, x: 10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 10 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <Typography variant="h6">
                                            <FormattedMessage id="quickHelp" />
                                        </Typography>
                                    </Box>
                                    <Box mt={2}>
                                        <SupportedHotkeys />
                                    </Box>
                                </motion.div>
                            ) : (
                                items.map(
                                    (item) =>
                                        selectedPage === item.type && (
                                            <motion.div
                                                style={{ height: '100%' }}
                                                key={item.type}
                                                initial={{ opacity: 0, x: 10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: 10 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                {item.content}
                                            </motion.div>
                                        )
                                )
                            )}
                        </AnimatePresence>
                    </Paper>
                </Box>
            </Box>
        </MuiModal>
    );
}
